import {useRouter} from 'next/router';
import {pathOr} from 'ramda';
import React, {useCallback, useEffect} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import {SEARCH_FILTERS} from '@/src/app/constants/search';
import useContentCreatorWidget from '@/src/app/store/contentCreatorWidget/useContentCreatorWidget';
import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import useLoading from '@/src/hooks/useLoading';
import useRoutePaths from '@/src/hooks/useRoutePaths';
import TextLocal from '@/src/textLocal/containers/TextLocal';

import {Button, LoadingWrapper, Separator, Text} from '../../ui';
import ContentCreator from '../components/ContentCreator';
import ContentCreatorCourses from '../components/ContentCreatorCourses';
import css from '../styles/ContentCreatorWidget.module.css';
import getContentCreatorResentCourses from '../utils/getContentCreatorResentCourses';
import requestGetContentCreator from '../utils/requestGetContentCreator';
import requestToggleFollow from '../utils/requestToggleFollow';

interface ContentCreatorWidgetProps {
  userId: string;
  excludedCourses?: Array<string>;
  isShowUserCourses?: boolean;
  isAllowToFollow?: boolean;
}

const ContentCreatorWidget = ({
  userId,
  excludedCourses,
  isShowUserCourses,
  isAllowToFollow,
}: ContentCreatorWidgetProps) => {
  const {
    setCourses,
    contentCreator,
    courses,
    toggleContentCreatorFollow,
    redirectToLoginPageWithReturn,
    setContentCreator,
  } = useContentCreatorWidget();
  const {user} = useUserActions();
  const currentUserId = pathOr(null, ['user', 'id'], user);
  const {push} = useRouter();
  const {SEARCH_COURSES} = useRoutePaths();

  const {isLoading: isLoadingToggleFollow, setLoading: setLoadingToggleFollow} =
    useLoading();

  const {isLoading, setLoading} = useLoading();

  const requestContentCreatorCourses = useCallback(async () => {
    setLoading(true);
    const {status, responseBody} = await getContentCreatorResentCourses(
      userId,
      [
        ...(excludedCourses
          ? [
              {
                name: 'exclude_course_ids',
                value: excludedCourses,
              },
            ]
          : []),
      ]
    );

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setCourses(responseBody);
    }

    setLoading(false);
  }, [userId, excludedCourses]);

  const getContentCreator = useCallback(async () => {
    const {status, responseBody} = await requestGetContentCreator(userId);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setContentCreator(responseBody);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      isShowUserCourses && requestContentCreatorCourses();
      getContentCreator();
    }
  }, [userId]);

  const handleFollowToggleClick = useCallback(async () => {
    if (!getAuthorizationToken()) {
      redirectToLoginPageWithReturn();
    }

    setLoadingToggleFollow(true);
    const {status} = await requestToggleFollow(
      userId,
      contentCreator.isFollowed
    );

    if (status === RESPONSE_STATUSES.SUCCESS) {
      toggleContentCreatorFollow(!contentCreator.isFollowed);
    }

    setLoadingToggleFollow(false);
  }, [contentCreator.isFollowed, userId]);

  const handleSeeAllCoursesClick = useCallback(() => {
    push({
      pathname: SEARCH_COURSES.getLink(),
      query: `${SEARCH_FILTERS.USER_ID}=${userId}`,
    });
  }, [userId]);

  return (
    <div className={css.contentCreatorWidget}>
      <LoadingWrapper isLoading={isLoading} hideContentBeforeLoad>
        <>
          {contentCreator && (
            <div className={css.contentCreatorWrapper}>
              <div className={css.contentCreator}>
                <ContentCreator
                  isClinic={contentCreator.isClinic}
                  slug={contentCreator.slug}
                  name={contentCreator.name}
                  image={contentCreator.image}
                  rating={contentCreator.rating}
                  aboutContentCreator={contentCreator.about}
                  isFollowed={contentCreator.isFollowed}
                  onToggleFollowClick={handleFollowToggleClick}
                  isLoadingFollow={isLoadingToggleFollow}
                  withFollowButton={currentUserId !== userId && isAllowToFollow}
                />
              </div>
            </div>
          )}

          {Boolean(courses.length) && (
            <>
              <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
              <div className={css.otherCoursesWrapper}>
                <div className={css.otherCoursesTitleWrapper}>
                  <Text
                    fontSize={Text.FONT_SIZE.SIZE_16}
                    fontWeight={Text.FONT_WEIGHT.BOLD}
                  >
                    <TextLocal id={'courseDetails.other-courses'} />
                  </Text>
                </div>
                <div className={css.otherCourses}>
                  <ContentCreatorCourses courses={courses} />
                </div>
                <Separator borderWeight={Separator.BORDER_WEIGHT.THIN} />
              </div>
              <div className={css.coursesButtonWrapper}>
                <div>
                  <Button
                    onClick={handleSeeAllCoursesClick}
                    color={Button.COLOR.SECONDARY}
                    border={Button.BORDER.BOLD}
                    height={Button.HEIGHT.SMALL}
                  >
                    <TextLocal id={'courseDetails.all-courses'} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      </LoadingWrapper>
    </div>
  );
};

export default ContentCreatorWidget;
